import {
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
  useId,
  FocusEvent,
  ReactNode,
} from 'react';

import classNames from 'classnames';
import escapeRegExp from 'lodash/escapeRegExp';
import useAutocomplete, {
  createFilterOptions,
} from '@mui/material/useAutocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Popper from '@mui/material/Popper';
import FormControl from '@mui/material/FormControl';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import { Box } from '../../Box';
import { Icon } from '../../Icon';
import { Label, Preloader } from '../common';
import { Paper } from '../../Paper';
import { TreeViewItem as Item, TreeView } from '../../TreeView';
import { useIsInsideContainer } from '../../hooks';
import { Typography } from '../../Typography';
import { CircularProgress } from '../../CircularProgress';

import { ListBox } from './ListBox';
import { useStyles } from './TreeViewAutocomplete.styles';

const MIN_INPUT_VALUE_LENGTH = 3;

type TreeNode<T> = {
  children?: Option<T>[];
};

interface Filter {
  limit: number;
  offset: number;
  query: string;
}

type Option<T> = Omit<T, 'children'> & TreeNode<T>;

interface TreeViewAutocompleteProps<T> {
  id?: string;
  disabled?: boolean;
  name?: string;
  label?: string;
  labelIcon?: ReactNode;
  tooltip?: ReactNode;
  required?: boolean;
  options: Option<T>[];
  noOptionsText?: string;
  placeholder?: string;
  clearable?: boolean;
  limit?: number;
  getOptionLabel?: (option: T) => string;
  value: string | undefined;
  onChange: (value?: string, item?: Item) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onSearch?: (filter: Filter) => Promise<T[]>;
  onChildrenLoad?: (unitId: string) => Promise<T[]>;
  onLoadTree?: (unitId: string) => Promise<Option<T>[]>;
  error?: boolean;
  helperText?: string;
  loading?: boolean;
  testId?: string;
  renderSearchOption?: (option: T, highlightedLabel: ReactNode) => ReactNode;
  renderOption?: (item: Item) => ReactNode;
  withoutMargins?: boolean;
}

/**
 * Компонент TreeViewAutocomplete представляет собой универсальное поле ввода, поддерживающее выбор данных в древовидной структуре.
 * Он объединяет функциональность автодополнения с древовидным представлением для вложенных опций, что делает его подходящим
 * для приложений, требующих иерархического выбора данных, таких как организационные диаграммы, структуры файловых директорий и т.д.
 *
 * @param id - Атрибут ID для поля ввода.
 * @param disabled - Если true, компонент отключен.
 * @param name - Имя компонента.
 * @param label - Подпись для поля ввода.
 * @param labelIcon - Иконка для метки.
 * @param tooltip - Необязательная подсказка для отображения с подписью.
 * @param required - Если true, поле ввода отмечено как обязательное.
 * @param options - Массив опций для отображения в древовидном представлении.
 * @param noOptionsText - Текст для отображения, когда никакие опции не соответствуют введенному значению.
 * @param placeholder - Текст заполнителя для поля ввода.
 * @param clearable - Если true, компонент можно очистить.
 * @param limit - Максимальное количество загружаемых опций за один раз.
 * @param getOptionLabel - Функция для извлечения текста подписи из опции.
 * @param value - Текущее значение компонента.
 * @param onChange - Колбэк-функция для обработки изменений выбранного значения.
 * @param onBlur - Колбэк-функция для обработки события потери фокуса полем ввода.
 * @param onSearch - Асинхронная функция для загрузки опций на основе введенного значения.
 * @param onLoadTree - Асинхронная функция для загрузки дочерних узлов дерева.
 * @param onChildrenLoad - Асинхронная функция для загрузки дочерних узлов дерева, обычно используется для ленивой загрузки.
 * @param testId - Тестовый идентификатор для поля ввода, полезен для целей тестирования.
 * @param error - Если true, поле ввода отмечено как имеющее ошибку.
 * @param helperText - Необязательный текст-помощь для отображения под полем ввода.
 * @param withoutMargins - Необязательный флаг, для отключения внешних отступов.
 * @param loading - Если true, поле ввода показывает состояние загрузки.
 * @param renderSearchOption - Кастомный метод рендера опции в списке поиска.
 * @param renderOption - Кастомный метод рендера элементов в древовидном представлении.
 */

export const TreeViewAutocomplete = <
  T extends {
    id: string;
    label: string;
    hasChildrenToLoad?: boolean;
    disabled?: boolean;
  },
>({
  id,
  disabled = false,
  label,
  labelIcon,
  tooltip,
  options,
  placeholder = '',
  clearable = true,
  required = false,
  getOptionLabel = (option) => option.label || '',
  noOptionsText = 'Ничего не найдено',
  onChange,
  onBlur,
  onSearch,
  onChildrenLoad,
  onLoadTree,
  value,
  error = false,
  helperText = '',
  loading = false,
  testId = 'test-id-tree-view-autocomplete',
  limit = 50,
  withoutMargins = false,
  renderSearchOption,
  renderOption,
  ...rest
}: TreeViewAutocompleteProps<T>) => {
  const { classes } = useStyles();
  const reactId = useId();
  const inputId = useMemo(() => {
    return id ?? reactId;
  }, [id, reactId]);

  const [isLoading, setLoading] = useState(false);
  const [isPaginationLoading, setPaginationLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [unitTree, setUnitTree] = useState<Option<T>[]>(options);
  const [loadedOptions, setLoadedOptions] = useState<T[]>([]);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [cachedValue, setCachedValue] = useState('');
  const filterRef = useRef<Filter>({
    limit,
    offset: 0,
    query: '',
  });
  const isInsideContainer = useIsInsideContainer();

  const isDisabled = disabled || loading;

  const treeOpen = !isDisabled && open;

  useEffect(() => {
    if (treeOpen && onLoadTree && value) {
      setLoading(true);
      onLoadTree(value)
        .then((result) => {
          setUnitTree(result);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [onLoadTree, treeOpen, value]);

  const handleChange = useCallback(
    (nextValue?: string, item?: Item) => {
      onChange(nextValue, item);
      setOpen(false);
    },
    [onChange],
  );

  const handleClearClick = useCallback(
    (e: React.MouseEvent) => {
      if (!focused) {
        e.preventDefault();
        e.stopPropagation();
      }
      setInputValue('');
      setCachedValue('');
      onChange('');
    },
    [focused, onChange],
  );

  const getFlatTree = useCallback((array: Option<T>[]) => {
    let result: T[] = [];
    array.forEach((item) => {
      if (item.children instanceof Array) {
        const { children, ...itemRest } = item;
        result.push(itemRest as T);
        result = [...result, ...getFlatTree(children)];
      } else {
        result.push(item as T);
      }
    });
    return result;
  }, []);

  const flatOptions = useMemo(
    () => getFlatTree(options).sort((a, b) => (a.label > b.label ? 1 : -1)),
    [getFlatTree, options],
  );

  useEffect(() => {
    setUnitTree(options);
  }, [options]);

  useEffect(() => {
    if (value) {
      const foundOption = flatOptions.find((opt) => opt.id === value);
      if (foundOption) {
        setInputValue(foundOption.label);
        setCachedValue(foundOption.label);
      } else if (cachedValue) {
        setInputValue(cachedValue);
      } else {
        setInputValue(value);
        setCachedValue(value);
      }
    }
  }, [value, flatOptions, cachedValue]);

  const handleSearch = useCallback(
    (newValue: string) => {
      if (newValue && onSearch) {
        setLoading(true);
        filterRef.current = {
          offset: 0,
          limit: 50,
          query: '',
        };
        setHasMore(true);
        onSearch({
          query: newValue,
          limit: 50,
          offset: 0,
        })
          .then((result) => {
            setLoadedOptions(result);
            setHasMore(result.length >= limit);
            setLoading(false);
          })
          .catch(() => setLoading(false));
      }
    },
    [onSearch, limit],
  );

  useEffect(() => {
    if (!value) {
      setInputValue('');
      setCachedValue('');
      setLoadedOptions([]);
    }
  }, [value]);

  const { getRootProps, getInputProps, groupedOptions, getOptionProps } =
    useAutocomplete({
      id: inputId,
      options: onSearch ? loadedOptions : flatOptions,
      value: (value as unknown as T) || null,
      clearOnBlur: false,
      getOptionLabel,
      blurOnSelect: true,
      isOptionEqualToValue: (option) => option.id === value,
      disabled: isDisabled,
      inputValue,
      onChange: (event, val) => {
        if (val && onLoadTree) {
          setLoading(true);
          onLoadTree(val.id)
            .then((result) => {
              setUnitTree(result);

              const unitsFounded = getFlatTree(result);
              const unitFound = unitsFounded.find((unit) => unit.id === val.id);

              if (unitFound?.disabled) return;
              handleChange(val.id, unitFound ?? val);
              setInputValue(val.label);
              setCachedValue(val.label);
            })
            .catch(() => {
              handleChange(val ? val.id : '', val ?? undefined);
              setInputValue(val ? val.label : '');
              setCachedValue(val ? val.label : '');
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          handleChange(val ? val.id : '', val ?? undefined);
          setInputValue(val ? val.label : '');
          setCachedValue(val ? val.label : '');
        }
      },
      filterOptions: createFilterOptions<T>({ ignoreAccents: false }),
      onInputChange: (event, nextValue, reason) => {
        if (reason !== 'reset') {
          handleSearch?.(nextValue);
          setInputValue(nextValue);
        }

        if (nextValue !== '') {
          setOpen(true);
        }
      },
    });

  const popperRef = useRef<null | HTMLDivElement>(null);
  const textInputRef = useRef<null | HTMLDivElement>(null);
  const isPopperOpen =
    treeOpen && (!inputValue || inputValue.length >= MIN_INPUT_VALUE_LENGTH);

  const getHighlightedLabel = useCallback(
    (currentValue: string) => {
      const words = currentValue.split(
        new RegExp(`(${escapeRegExp(inputValue)})`, 'gi'),
      );

      return (
        <span>
          {words.map((part, i) => (
            <Typography
              className={classes.optionText}
              component="span"
              color={
                part.toLowerCase() === inputValue.toLowerCase()
                  ? 'text.light.primary'
                  : 'text.light.tertirary'
              }
              key={i}
            >
              {part}
            </Typography>
          ))}
        </span>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputValue],
  );

  const handleNext = () => {
    if (!onSearch || isPaginationLoading) return;

    const newFilter = {
      ...filterRef.current,
      query: inputValue,
      offset: filterRef.current.offset + filterRef.current.limit,
    };

    filterRef.current = newFilter;
    setPaginationLoading(true);
    onSearch(newFilter)
      .then((result) => {
        setLoadedOptions((prev) => [...prev, ...result]);
        setPaginationLoading(false);
        setHasMore(result.length >= newFilter.limit);
      })
      .catch(() => setPaginationLoading(false));
  };

  const onFocus = useCallback(() => {
    setFocused(true);
    setInputValue('');
    setCachedValue(inputValue);
  }, [inputValue]);

  const handlePopperOutsideClick = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      const isTargetInsidePopper = popperRef?.current?.contains(target);
      const wasTextFieldClicked =
        textInputRef?.current?.parentNode?.contains(target) ||
        textInputRef?.current?.contains(target);

      if (
        !wasTextFieldClicked &&
        open &&
        !isTargetInsidePopper &&
        !isInsideContainer
      )
        setOpen(false);
    },
    [open, popperRef, isInsideContainer],
  );

  const handlePopperMouseDown = useCallback((event: React.MouseEvent) => {
    // Не даем сработать событию onBlur() у <TextField>, если кликнули на элементы внутри <Popper>
    event.preventDefault();
  }, []);

  const onClick = useCallback(() => {
    setOpen(true);
    setFocused(true);
  }, []);

  const onBlurInner = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      const { relatedTarget } = event;
      const containsTarget = popperRef?.current?.contains(
        relatedTarget as Element,
      );
      if (!containsTarget) {
        setOpen(false);
      }
      setFocused(false);

      setInputValue(cachedValue);
    },
    [cachedValue, onBlur],
  );

  const onTreeViewSelect = useCallback(
    (valueId: string, newLabel?: string, item?: Item) => {
      handleChange(valueId, item);
      setInputValue(newLabel || '');
      setCachedValue(newLabel || '');
      setTimeout(() => {
        textInputRef.current?.blur();
      }, 0);
    },
    [handleChange, textInputRef],
  );

  const isShowNoOptionText = !isLoading && groupedOptions.length === 0;
  const isShowMenuOptions = !isLoading && groupedOptions.length !== 0;

  return (
    <div {...getRootProps()}>
      <FormControl
        className={classNames(
          classes.treeViewAutocompleteInput,
          withoutMargins && classes.withoutMargins,
          'aqa_tree_view_autocomplete_input',
        )}
      >
        {label && (
          <Label
            label={label}
            required={required}
            tooltip={tooltip}
            icon={labelIcon}
            inputId={inputId}
          />
        )}
        <TextField
          id={inputId}
          data-testid={testId}
          {...rest}
          disabled={isDisabled}
          fullWidth
          variant="outlined"
          autoComplete="off"
          error={error}
          helperText={helperText}
          placeholder={placeholder || label}
          onFocus={onFocus}
          onClick={onClick}
          value={loading ? 'Загрузка...' : inputValue}
          onBlur={onBlurInner}
          inputRef={textInputRef}
          slotProps={{
            htmlInput: {
              className: classes.input,
              ...getInputProps(),
            },
            input: {
              ref: setAnchorEl,
              className: `${classes.inputRoot} aqa_select`,
              disabled: isDisabled,
              endAdornment: loading ? (
                <Preloader />
              ) : (
                <InputAdornment className={classes.endAdornment} position="end">
                  {inputValue && (focused || clearable) && (
                    <Box
                      className={classes.clearButton}
                      testId="tree-view-autocomplete-clear"
                      onClick={handleClearClick}
                    >
                      <Icon
                        name="dismiss_substract"
                        color="stroke.secondary"
                        size="24"
                      />
                    </Box>
                  )}
                  <Box className={classes.arrowButton}>
                    <Box
                      m="4"
                      className={classNames({
                        [classes.popupIndicatorOpen]: treeOpen,
                      })}
                    >
                      <Icon
                        name="arrow_triangle_down"
                        size="16"
                        color="text.dark.secondary"
                      />
                    </Box>
                  </Box>
                </InputAdornment>
              ),
            },
          }}
        />
      </FormControl>

      <ClickAwayListener onClickAway={handlePopperOutsideClick}>
        <Popper
          role="presentation"
          open={isPopperOpen}
          ref={popperRef}
          anchorEl={anchorEl}
          style={{
            width: anchorEl ? anchorEl.clientWidth : 'auto',
            zIndex: 1400,
          }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 5],
              },
            },
          ]}
          onMouseDown={handlePopperMouseDown}
        >
          <Paper elevation={2} className={classes.paper}>
            <Box py="8">
              {inputValue ? (
                <>
                  {isLoading && (
                    <Box display="flex" justifyContent="center" p="16">
                      <CircularProgress size={30} />
                    </Box>
                  )}

                  {isShowMenuOptions && (
                    <ListBox
                      hasMore={hasMore}
                      dataLength={groupedOptions.length}
                      next={handleNext}
                      loading={isPaginationLoading}
                    >
                      {groupedOptions.map((option, index) => {
                        const highlightedLabel = getHighlightedLabel(
                          getOptionLabel(option),
                        );

                        return (
                          <MenuItem
                            className={classes.menuItem}
                            {...getOptionProps({ option, index })}
                            key={option.id}
                          >
                            {renderSearchOption
                              ? renderSearchOption(option, highlightedLabel)
                              : highlightedLabel}
                          </MenuItem>
                        );
                      })}
                    </ListBox>
                  )}

                  {isShowNoOptionText && (
                    <Box className={classes.menuItem}>
                      <Typography
                        color="text.light.tertirary"
                        className={classes.optionText}
                      >
                        {noOptionsText}
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  {isLoading && (
                    <Box display="flex" justifyContent="center" p="16">
                      <CircularProgress size={30} />
                    </Box>
                  )}
                  {!isLoading && (
                    <TreeView
                      items={unitTree}
                      selected={value || ''}
                      selectOnShrink={false}
                      showRulers={false}
                      showCounters={false}
                      onSelect={onTreeViewSelect}
                      onChildrenLoad={onChildrenLoad}
                      renderOption={renderOption}
                    />
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Popper>
      </ClickAwayListener>
    </div>
  );
};
